import { useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Construction } from 'lucide-react'

// Map routes to friendly names and descriptions
const routeInfo: Record<string, { title: string; description: string }> = {
  // Operations
  '/operations/hangar-view': {
    title: 'Hangar View',
    description: 'Interactive 3D hangar visualization with real-time aircraft positioning.',
  },
  '/operations/schedule': {
    title: "Today's Schedule",
    description: 'Timeline view of arrivals, departures, and aircraft movements.',
  },
  '/allocations': {
    title: 'Current Allocations',
    description: 'View and manage current aircraft positions and space allocations.',
  },
  '/conflicts': {
    title: 'Conflicts & Alerts',
    description: 'Space conflicts, scheduling issues, and system alerts.',
  },
  // Bookings
  '/transients': {
    title: 'Transient Stays',
    description: 'Manage temporary aircraft bookings and visitor reservations.',
  },
  '/quick-fit': {
    title: 'Quick Fit Tool',
    description: 'Find available space for aircraft with instant fit checking.',
  },
  '/availability': {
    title: 'Availability Calendar',
    description: 'Visual calendar showing space availability across all units.',
  },
  '/waitlist': {
    title: 'Waitlist',
    description: 'Manage clients waiting for specific space or unit types.',
  },
  // Space
  '/units': {
    title: 'Units',
    description: 'View and manage all units across buildings.',
  },
  '/space-pool': {
    title: 'Availability Pool',
    description: 'Daily space pool showing available square footage.',
  },
  '/space-contributions': {
    title: 'Space Contributions',
    description: 'Manage space offerings for transient availability.',
  },
  // Contracts
  '/leases': {
    title: 'Lease Contracts',
    description: 'Manage long-term hangar lease agreements.',
  },
  '/leases/expiring': {
    title: 'Expiring Leases',
    description: 'Leases expiring within the next 90 days.',
  },
  '/leases/renewals': {
    title: 'Renewals Pipeline',
    description: 'Track lease renewal process from offer to signature.',
  },
  '/leases/templates': {
    title: 'Contract Templates',
    description: 'Manage standard lease contract templates.',
  },
  // Billing
  '/invoices': {
    title: 'Invoices',
    description: 'Create and manage customer invoices.',
  },
  '/payments': {
    title: 'Payments',
    description: 'Track and record customer payments.',
  },
  '/ar-aging': {
    title: 'AR Aging',
    description: 'Accounts receivable aging report and collections.',
  },
  // Reports
  '/reports/occupancy': {
    title: 'Occupancy Reports',
    description: 'Occupancy trends and utilization analysis.',
  },
  '/reports/availability': {
    title: 'Availability Reports',
    description: 'Space availability analysis and forecasting.',
  },
  '/reports/capacity': {
    title: 'Capacity Reports',
    description: 'Capacity analysis and planning reports.',
  },
  '/reports/revenue': {
    title: 'Revenue Reports',
    description: 'Revenue analysis by source, period, and location.',
  },
  // Settings
  '/settings/pricing': {
    title: 'Pricing Rules',
    description: 'Configure rate tables and pricing rules.',
  },
  '/settings/users': {
    title: 'Users & Roles',
    description: 'Manage team members and permissions.',
  },
  '/settings/integrations': {
    title: 'Integrations',
    description: 'Connect with external services and APIs.',
  },
  // Placeholders from original
  '/map': {
    title: 'Map View',
    description: 'Interactive 3D hangar visualization.',
  },
  '/aircraft': {
    title: 'Aircraft Registry',
    description: 'View and manage aircraft in your fleet.',
  },
  '/settings': {
    title: 'Settings',
    description: 'Account and organization settings.',
  },
  '/buildings/offices': {
    title: 'Offices',
    description: 'Office building management.',
  },
  '/buildings/units': {
    title: 'Building Units',
    description: 'Cross-building unit management.',
  },
}

export function ComingSoonPage() {
  const location = useLocation()
  const path = location.pathname

  // Get route-specific info or use defaults
  const info = routeInfo[path] || {
    title: 'Coming Soon',
    description: 'This feature is under development.',
  }

  // Generate breadcrumb from path
  const pathSegments = path.split('/').filter(Boolean)
  const breadcrumb = pathSegments
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '))
    .join(' > ')

  return (
    <div className="space-y-6">
      {breadcrumb && (
        <div className="text-sm text-muted-foreground">{breadcrumb}</div>
      )}
      <h1 className="text-3xl font-bold tracking-tight">{info.title}</h1>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Construction className="h-5 w-5" />
            Coming Soon
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{info.description}</p>
          <p className="mt-4 text-sm text-muted-foreground">
            This feature is currently under development and will be available soon.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
